import type { Category } from '../types';
import { FACTS } from '../facts';

const LANE = `$${FACTS.lanePassPerDeliveryUsd}`;
const LANE_MAX = `$${FACTS.lanePassWeeklyMaxUsd}`;
const GRACE = `${FACTS.lanePassGraceDays}`;

export const deductions: Category = {
  slug: 'deductions',
  half: 'business',
  accent: '#2E9E3A',
  icon: 'percent',
  label: { en: "Deductions you might miss", es: "Deducciones que se te pueden pasar" },
  blurb: {
    en: "Mileage is the big one. Phone, insulated bags, tolls, parking — track everything, all year.",
    es: "El millaje es la grande. Teléfono, bolsas térmicas, peajes, estacionamiento — registra todo, todo el año.",
  },
  guides: [
    {
      slug: 'mileage',
      irsLinks: ['pub-463', 'schedule-c'],
      related: ['recordkeeping-and-money/mileage-log-habit', 'deductions/tolls-and-parking', 'insurance-for-delivery/personal-auto-gap'],
      body: {
        en: {
          title: "Mileage: the big one",
          summary: "For most Runners, business miles are the largest deduction of the year, and the IRS only counts the ones you wrote down.",
          sections: [
            {
              heading: "What counts as business miles",
              bullets: [
                "From your first pickup of a shift to your last drop-off, including trips between stores and customers.",
                "Driving to the store for a delivery you've accepted.",
                "Trips to buy supplies for the business, like bags or a phone mount.",
                "Not the commute from home to your first stop, and not personal errands you fold into a shift. The rules on the first and last leg have nuance, so confirm with an accountant.",
              ],
            },
            {
              heading: "Two ways to deduct",
              paragraphs: [
                "Standard mileage: multiply your business miles by the IRS rate. The current rate is in IRS Pub. 463, and it changes, so don't memorize last year's. This covers fuel, maintenance, depreciation, and insurance in one number.",
                "Actual expenses: track every car cost and deduct the business percentage. More paperwork, sometimes a bigger number for expensive cars. Whichever you choose in a car's first business year affects later years, so pick with an accountant.",
                "Either way, tolls and parking are deducted separately.",
              ],
            },
            {
              heading: "The log is not optional",
              paragraphs: [
                "The IRS expects a contemporaneous record: date, miles, where, and why. A tracking app that runs during shifts works. So does a notebook on the dash. Reconstructing it in April from memory doesn't.",
              ],
            },
            {
              heading: "Fuel is not an extra deduction",
              paragraphs: [
                "If you use the standard rate, fuel is already inside it. Don't deduct gas on top. A lot of first-year Runners do this by accident.",
              ],
            },
          ],
          actions: [
            "Install a mileage app or put a notebook in the car today.",
            "Write down your odometer reading right now, and again on December 31.",
            "Look up the current rate in Pub. 463 so you can estimate this year's deduction.",
          ],
        },
        es: {
          title: "Millaje: la deducción grande",
          summary: "Para la mayoría de los Runners, las millas de negocio son la deducción más grande del año, y el IRS solo cuenta las que anotaste.",
          sections: [
            {
              heading: "Qué cuenta como millas de negocio",
              bullets: [
                "Desde tu primera recogida del turno hasta tu última entrega, incluyendo los trayectos entre tiendas y clientes.",
                "Manejar a la tienda por una entrega que ya aceptaste.",
                "Viajes para comprar suministros del negocio, como bolsas o un soporte para el teléfono.",
                "No cuenta el trayecto de tu casa a la primera parada, ni los mandados personales que metas en un turno. Las reglas del primer y último tramo tienen matices, así que confírmalo con un contador.",
              ],
            },
            {
              heading: "Dos formas de deducir",
              paragraphs: [
                "Millaje estándar: multiplica tus millas de negocio por la tarifa del IRS. La tarifa actual está en la Publicación 463 del IRS, y cambia, así que no memorices la del año pasado. Cubre gasolina, mantenimiento, depreciación y seguro en una sola cifra.",
                "Gastos reales: registra cada costo del auto y deduce el porcentaje de negocio. Más papeleo, a veces una cifra mayor en autos caros. Lo que elijas en el primer año de uso comercial de un auto afecta los años siguientes, así que decide con un contador.",
                "De cualquier forma, los peajes y el estacionamiento se deducen aparte.",
              ],
            },
            {
              heading: "El registro no es opcional",
              paragraphs: [
                "El IRS espera un registro hecho en el momento: fecha, millas, dónde y por qué. Una app de seguimiento que corra durante los turnos funciona. También una libreta en el tablero. Reconstruirlo de memoria en abril, no.",
              ],
            },
            {
              heading: "La gasolina no es una deducción extra",
              paragraphs: [
                "Si usas la tarifa estándar, la gasolina ya está incluida. No deduzcas el combustible además. Muchos Runners en su primer año lo hacen por accidente.",
              ],
            },
          ],
          actions: [
            "Instala una app de millaje o pon una libreta en el auto hoy.",
            "Anota la lectura de tu odómetro ahora mismo, y otra vez el 31 de diciembre.",
            "Busca la tarifa actual en la Publicación 463 para estimar la deducción de este año.",
          ],
        },
      },
    },
    {
      slug: 'phone-and-data',
      irsLinks: ['schedule-c'],
      related: ['your-gear/phone-mount-and-power', 'recordkeeping-and-money/receipts-and-bookkeeping'],
      body: {
        en: {
          title: "Phone and data",
          summary: "Your phone runs the whole job; the business share of the bill, and the accessories, are deductible.",
          sections: [
            {
              heading: "The business share",
              paragraphs: [
                "You can deduct the portion of your phone and data plan you use for delivery work. If the phone is used for both, estimate the business percentage honestly and stick with it. Sixty percent business means sixty percent of the bill.",
                "A second line or a phone used only for work is simpler: the whole bill is business.",
              ],
            },
            {
              heading: "What else fits here",
              bullets: [
                "The phone itself, or the business share of it, if you bought it for the work.",
                "A car mount, charging cables, and a battery pack.",
                "A mileage-tracking app subscription.",
                "Repairs or a screen protector for a work phone.",
              ],
            },
            {
              heading: "How to back it up",
              bullets: [
                "Keep twelve months of phone bills in your receipts folder. Most carriers let you download PDFs.",
                "Write your business percentage and how you got it in a note, once, dated. A month of screen-time stats is plenty.",
                "Pay for accessories with your business debit card so they're already in the right account.",
              ],
            },
            {
              heading: "Where it goes",
              paragraphs: [
                "The plan goes on Schedule C under utilities or other expenses; accessories under supplies. Your software or accountant will place them. What matters is that you kept the bills.",
              ],
            },
          ],
          actions: [
            "Download this year's phone bills so far.",
            "Estimate your business percentage and write the reasoning in a dated note.",
            "Move your next accessory purchase to the business card.",
          ],
        },
        es: {
          title: "Teléfono y datos",
          summary: "Tu teléfono hace todo el trabajo; la parte de negocio de la factura, y los accesorios, son deducibles.",
          sections: [
            {
              heading: "La parte de negocio",
              paragraphs: [
                "Puedes deducir la porción de tu plan de teléfono y datos que usas para el trabajo de entregas. Si el teléfono es para ambas cosas, estima el porcentaje de negocio con honestidad y mantente en él. Sesenta por ciento de negocio significa sesenta por ciento de la factura.",
                "Una segunda línea o un teléfono usado solo para trabajar es más simple: toda la factura es de negocio.",
              ],
            },
            {
              heading: "Qué más entra aquí",
              bullets: [
                "El teléfono en sí, o su parte de negocio, si lo compraste para el trabajo.",
                "Un soporte para el auto, cables de carga y una batería externa.",
                "La suscripción a una app de registro de millaje.",
                "Reparaciones o un protector de pantalla para un teléfono de trabajo.",
              ],
            },
            {
              heading: "Cómo respaldarlo",
              bullets: [
                "Guarda doce meses de facturas del teléfono en tu carpeta de recibos. La mayoría de las compañías te deja descargar PDFs.",
                "Escribe tu porcentaje de negocio y cómo lo calculaste en una nota, una sola vez, con fecha. Un mes de estadísticas de tiempo de pantalla es suficiente.",
                "Paga los accesorios con tu tarjeta de débito del negocio para que ya queden en la cuenta correcta.",
              ],
            },
            {
              heading: "Dónde va",
              paragraphs: [
                "El plan va en el Anexo C bajo servicios u otros gastos; los accesorios bajo suministros. Tu software o tu contador los ubicará. Lo que importa es que guardaste las facturas.",
              ],
            },
          ],
          actions: [
            "Descarga las facturas del teléfono de este año hasta la fecha.",
            "Estima tu porcentaje de negocio y escribe el razonamiento en una nota con fecha.",
            "Haz tu próxima compra de accesorios con la tarjeta del negocio.",
          ],
        },
      },
    },
    {
      slug: 'bags-coolers-gear',
      irsLinks: ['schedule-c'],
      related: ['your-gear/coolers-and-bags', 'your-gear/cart-and-winter-gear', 'recordkeeping-and-money/receipts-and-bookkeeping'],
      body: {
        en: {
          title: "Bags, coolers, and gear",
          summary: "Everything you buy to keep groceries cold, dry, and upright is a business expense; keep the receipt.",
          sections: [
            {
              heading: "What fits",
              bullets: [
                "Insulated bags and coolers, plus ice packs.",
                "A folding cart or hand truck for apartment buildings.",
                "Rain gear, a reflective vest, work gloves, winter traction cleats.",
                "Trunk organizers, bungee cords, a small flashlight.",
                "A thermometer for checking cold-chain items.",
                "Replacement of any of the above when it wears out.",
              ],
            },
            {
              heading: "What doesn't",
              paragraphs: [
                "Everyday clothes and shoes, even if you only wear them to work. The IRS draws the line at things suitable for ordinary wear. A branded vest or safety gear is fine; a nice pair of sneakers isn't.",
                "Groceries for your customers. You never pay for those; the Grocery List Plus-funded virtual card does.",
              ],
            },
            {
              heading: "Small vs. big purchases",
              paragraphs: [
                "Most gear is cheap enough to expense in the year you buy it. If you ever buy something expensive and long-lasting, there are rules about spreading the cost over years versus deducting it at once. Confirm with an accountant before you assume.",
              ],
            },
            {
              heading: "Make it easy on yourself",
              bullets: [
                "Buy gear with the business debit card.",
                "Photograph the receipt the same day and drop it in a 'gear' folder.",
                "Once a year, list what you own and what you replaced. It doubles as an insurance inventory.",
              ],
            },
          ],
          actions: [
            "Go through your car and list the gear you already use for deliveries.",
            "Find the receipts you still have and file them; note the ones you've lost so you don't repeat it.",
            "Set up a 'gear' folder in your receipts app or a paper envelope in the glovebox.",
          ],
        },
        es: {
          title: "Bolsas, hieleras y equipo",
          summary: "Todo lo que compras para mantener la despensa fría, seca y en su lugar es un gasto de negocio; guarda el recibo.",
          sections: [
            {
              heading: "Qué entra",
              bullets: [
                "Bolsas térmicas y hieleras, más paquetes de hielo.",
                "Un carrito plegable o diablito para edificios de apartamentos.",
                "Ropa para lluvia, un chaleco reflectante, guantes de trabajo, crampones para el hielo.",
                "Organizadores de cajuela, cuerdas elásticas, una linterna pequeña.",
                "Un termómetro para revisar productos de cadena de frío.",
                "El reemplazo de cualquiera de los anteriores cuando se desgaste.",
              ],
            },
            {
              heading: "Qué no entra",
              paragraphs: [
                "Ropa y zapatos de uso diario, aunque solo los uses para trabajar. El IRS pone el límite en lo que sirve para uso ordinario. Un chaleco con logotipo o equipo de seguridad sí; unos tenis bonitos no.",
                "La despensa de tus clientes. Tú nunca la pagas; la paga la tarjeta virtual financiada por Lista de Compras Plus.",
              ],
            },
            {
              heading: "Compras pequeñas vs. grandes",
              paragraphs: [
                "La mayoría del equipo es lo bastante barato como para deducirlo en el año en que lo compras. Si algún día compras algo caro y duradero, hay reglas sobre repartir el costo en varios años en lugar de deducirlo de una vez. Confírmalo con un contador antes de dar algo por hecho.",
              ],
            },
            {
              heading: "Póntelo fácil",
              bullets: [
                "Compra el equipo con la tarjeta de débito del negocio.",
                "Fotografía el recibo el mismo día y guárdalo en una carpeta de 'equipo'.",
                "Una vez al año, haz una lista de lo que tienes y lo que reemplazaste. También sirve como inventario para el seguro.",
              ],
            },
          ],
          actions: [
            "Revisa tu auto y haz una lista del equipo que ya usas para las entregas.",
            "Busca los recibos que aún tengas y archívalos; anota los que perdiste para no repetirlo.",
            "Crea una carpeta de 'equipo' en tu app de recibos o un sobre de papel en la guantera.",
          ],
        },
      },
    },
    {
      slug: 'tolls-and-parking',
      irsLinks: ['pub-463', 'schedule-c'],
      related: ['deductions/mileage', 'recordkeeping-and-money/mileage-log-habit'],
      body: {
        en: {
          title: "Tolls and parking",
          summary: "Small, frequent, and easy to forget; tolls and parking add up and are deductible on top of mileage.",
          sections: [
            {
              heading: "Why they're separate",
              paragraphs: [
                "The standard mileage rate covers the cost of running your car. It does not cover tolls or parking fees you pay while working. Those are deducted in addition, whichever mileage method you use.",
              ],
            },
            {
              heading: "What counts",
              bullets: [
                "Tolls on the way to a store or a customer during a shift.",
                "Parking meters, garages, and lot fees at a store or a customer's building.",
                "A toll transponder's monthly fee, for the business share.",
              ],
            },
            {
              heading: "What doesn't",
              bullets: [
                "Parking tickets and traffic fines. Never deductible, no matter when you got them.",
                "Tolls on your commute from home to your first stop, under the general rule. Confirm with an accountant if your situation is unusual.",
              ],
            },
            {
              heading: "How to track them",
              bullets: [
                "Link your toll transponder to the business debit card and download the monthly statement.",
                "For cash or meter parking, log it in the same app or notebook as your miles: date, amount, and which delivery.",
                "Snap a photo of any garage receipt before you drive off.",
                "If a customer's building charges for parking, note it on that delivery so it's easy to explain later.",
              ],
            },
          ],
          actions: [
            "Download your toll account history for the year so far.",
            "Add a 'tolls/parking' column to your mileage log.",
            "Switch your transponder's payment method to the business card.",
          ],
        },
        es: {
          title: "Peajes y estacionamiento",
          summary: "Pequeños, frecuentes y fáciles de olvidar; los peajes y el estacionamiento se acumulan y son deducibles además del millaje.",
          sections: [
            {
              heading: "Por qué van aparte",
              paragraphs: [
                "La tarifa estándar por milla cubre el costo de operar tu auto. No cubre los peajes ni las tarifas de estacionamiento que pagas mientras trabajas. Esos se deducen adicionalmente, sin importar qué método de millaje uses.",
              ],
            },
            {
              heading: "Qué cuenta",
              bullets: [
                "Peajes camino a una tienda o a un cliente durante un turno.",
                "Parquímetros, estacionamientos y tarifas de lotes en una tienda o en el edificio de un cliente.",
                "La cuota mensual del transpondedor de peaje, en su parte de negocio.",
              ],
            },
            {
              heading: "Qué no cuenta",
              bullets: [
                "Multas de estacionamiento y de tránsito. Nunca son deducibles, sin importar cuándo te las pusieron.",
                "Peajes en el trayecto de tu casa a la primera parada, según la regla general. Confírmalo con un contador si tu situación es poco común.",
              ],
            },
            {
              heading: "Cómo registrarlos",
              bullets: [
                "Vincula tu transpondedor de peaje a la tarjeta de débito del negocio y descarga el estado de cuenta mensual.",
                "Para estacionamiento en efectivo o parquímetro, regístralo en la misma app o libreta que tus millas: fecha, monto y a qué entrega corresponde.",
                "Tómale foto a cualquier recibo de estacionamiento antes de arrancar.",
                "Si el edificio de un cliente cobra estacionamiento, anótalo en esa entrega para que sea fácil de explicar después.",
              ],
            },
          ],
          actions: [
            "Descarga el historial de tu cuenta de peajes del año hasta la fecha.",
            "Agrega una columna de 'peajes/estacionamiento' a tu registro de millaje.",
            "Cambia el método de pago de tu transpondedor a la tarjeta del negocio.",
          ],
        },
      },
    },
    {
      slug: 'home-office-and-other',
      irsLinks: ['schedule-c'],
      related: ['insurance-for-delivery/the-endorsement-to-ask-for', 'recordkeeping-and-money/set-aside-for-taxes', 'deductions/mileage'],
      body: {
        en: {
          title: "Home office and other deductions",
          summary: "The home office deduction rarely fits delivery work; here's why, plus the smaller deductions that do.",
          sections: [
            {
              heading: "Home office: usually no",
              paragraphs: [
                "To claim a home office, part of your home must be used regularly and exclusively for the business, and it must be your principal place of business. Delivery work happens in your car and at the store. A kitchen table where you check the app now and then doesn't qualify.",
                "A dedicated room used only to run the business (bookkeeping, planning, storing gear) might qualify. That's an accountant conversation, not a guess.",
              ],
            },
            {
              heading: "Deductions that do fit",
              bullets: [
                `The lane pass: ${LANE} per delivery, capped at ${LANE_MAX} a week; $0 on zero-delivery weeks and approved time off; first ${GRACE} days free. It's its own line on every pay statement; total it for the year.`,
                "The business share of a delivery or rideshare endorsement on your auto insurance.",
                "Bank fees on your business account, and any bookkeeping or tax software.",
                "Tax preparation fees for the business portion of your return.",
                "A DBA filing, an LLC formation fee, or the LLC's annual state fee.",
                "Health insurance premiums, if you pay your own and qualify. This one has rules.",
              ],
            },
            {
              heading: "Often asked about, doesn't fit",
              bullets: [
                "Meals you eat on shift. Eating while working isn't a business meal.",
                "Your regular clothes and shoes.",
                "Traffic and parking tickets.",
                "The value of your own time.",
              ],
            },
            {
              heading: "The honest summary",
              paragraphs: [
                "Your real deductions are mileage, the lane pass, phone, gear, tolls, parking, and insurance. Track those well and you've got most of it. Confirm anything borderline with an accountant before you claim it.",
              ],
            },
          ],
          actions: [
            "Total your lane pass lines from this year's pay statements.",
            "Pull your auto insurance declarations page and find the endorsement premium.",
            "List any borderline expenses and bring the list to an accountant.",
          ],
        },
        es: {
          title: "Oficina en casa y otras deducciones",
          summary: "La deducción por oficina en casa rara vez aplica al trabajo de entregas; aquí te explicamos por qué, más las deducciones pequeñas que sí aplican.",
          sections: [
            {
              heading: "Oficina en casa: normalmente no",
              paragraphs: [
                "Para reclamar una oficina en casa, parte de tu hogar debe usarse de forma regular y exclusiva para el negocio, y debe ser tu lugar principal de trabajo. El trabajo de entregas ocurre en tu auto y en la tienda. Una mesa de cocina donde revisas la app de vez en cuando no califica.",
                "Un cuarto dedicado que se usa solo para administrar el negocio (contabilidad, planeación, guardar equipo) podría calificar. Eso es una conversación con un contador, no una adivinanza.",
              ],
            },
            {
              heading: "Deducciones que sí aplican",
              bullets: [
                `El lane pass: ${LANE} por entrega, con tope de ${LANE_MAX} a la semana; $0 en semanas sin entregas y con tiempo libre aprobado; los primeros ${GRACE} días gratis. Es su propia línea en cada estado de pago; súmalo para el año.`,
                "La parte de negocio de un endoso de entregas o rideshare en tu seguro de auto.",
                "Comisiones bancarias de tu cuenta de negocio, y cualquier software de contabilidad o de impuestos.",
                "Honorarios de preparación de impuestos por la parte de negocio de tu declaración.",
                "El registro de un DBA, la tarifa de formación de una LLC o la cuota anual estatal de la LLC.",
                "Primas de seguro médico, si pagas el tuyo y calificas. Esta tiene reglas.",
              ],
            },
            {
              heading: "Se pregunta seguido, no aplica",
              bullets: [
                "Las comidas que tomas durante el turno. Comer mientras trabajas no es una comida de negocios.",
                "Tu ropa y zapatos de uso diario.",
                "Multas de tránsito y de estacionamiento.",
                "El valor de tu propio tiempo.",
              ],
            },
            {
              heading: "El resumen honesto",
              paragraphs: [
                "Tus deducciones reales son el millaje, el lane pass, el teléfono, el equipo, los peajes, el estacionamiento y el seguro. Regístralas bien y tendrás casi todo. Confirma cualquier cosa dudosa con un contador antes de reclamarla.",
              ],
            },
          ],
          actions: [
            "Suma las líneas del lane pass de los estados de pago de este año.",
            "Saca la página de declaraciones de tu seguro de auto y busca la prima del endoso.",
            "Anota los gastos dudosos y lleva la lista a un contador.",
          ],
        },
      },
    },
  ],
};
